import { useMemo, useState, type ComponentType } from 'react'
import { useSearchParams } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalAlert, MinimalButton, MinimalEmptyState } from '@ovasabi/ui-minimal'
import type { TenantProjectionStore } from '@ovasabi/frontend-kit'
import {
  BalanceCard,
  PageHeader,
  Screen,
  SectionHeading,
  StatTile,
  Tabs,
  TransactionRow,
  type PillOption,
} from '../../components/chow'
import { BankIcon, BowlIcon, CardIcon, WalletIcon } from '../../components/icons'
import { BALANCE_GRADIENT } from '../../styles/chowGradients'
import {
  prototypeStoreRegistry,
  useProfileWalletBalanceSnapshot,
  useWalletTransactionSnapshot,
} from '../../generated/prototypeRuntime'
import { isLiveRuntime } from '../../lib/runtime'
import { useBackNav, useChooseChow } from './ChooseChowContext'
import { isEarnedThisWeek, walletOwner } from './chefDashboard'
import { earningStateLabel, releaseStatusLabel, totalEarnedMinor, useChefPayouts, weekEarnedMinor } from './chefPayouts'
import { orderReference } from './orderReference'
import { ChefPayoutAccountForm } from './ChefPayoutAccount'

type CustomerTab = 'overview' | 'topup' | 'transactions' | 'methods'
type ChefTab = 'earnings' | 'payouts' | 'methods'

const customerTabs: readonly PillOption<CustomerTab>[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'topup', label: 'Top Up' },
  { value: 'transactions', label: 'Transactions' },
  { value: 'methods', label: 'Methods' },
]

const chefTabs: readonly PillOption<ChefTab>[] = [
  { value: 'earnings', label: 'Earnings' },
  { value: 'payouts', label: 'Payouts' },
  { value: 'methods', label: 'Bank' },
]

const Tiles = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: ${minimalVars.space.sm};
`

const List = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${minimalVars.space.xs};
`

type StoreRecord = Record<string, unknown>

const safeAmount = (r: StoreRecord) => Number(r.amount_minor ?? 0)

/* ── payment methods panel (customer + chef "methods"/"bank" tabs) ───── */

/*
 * A payment method row is: what it is, what it costs you, and whether it is the
 * one being used. Laid out as a two-column grid — icon, then everything else —
 * with the name and its badge sharing the first line. Putting the badge on the
 * row's trailing edge (the previous layout) squeezed the hint into a narrow
 * column beside it, so "Balance ₦0 · used first at checkout" wrapped to two
 * ragged lines while a third of the row sat empty.
 */
const MethodRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  column-gap: ${minimalVars.space.xs};
  row-gap: 2px;
  padding: ${minimalVars.space.xs};
  border-radius: ${minimalVars.radius.md};
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
`

const MethodMeta = styled.div`
  min-width: 0;
  display: grid;
  gap: 3px;
`

/** Name and badge share a line; the name truncates before the badge shrinks. */
const MethodTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${minimalVars.space.xs};
  min-width: 0;
`

const MethodName = styled.span`
  min-width: 0;
  font-weight: ${minimalVars.typography.weightSemibold};
  font-size: ${minimalVars.typography.bodySize};
  color: ${minimalVars.color.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MethodHint = styled.span`
  font-size: ${minimalVars.typography.captionSize};
  line-height: 1.35;
  color: ${minimalVars.color.textTertiary};
`

const MethodBadge = styled.span<{ $tone: 'active' | 'available' }>`
  flex-shrink: 0;
  font-size: ${minimalVars.typography.metaSize};
  font-weight: ${minimalVars.typography.weightSemibold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: ${minimalVars.radius.pill};
  white-space: nowrap;
  color: ${({ $tone }) => ($tone === 'active' ? minimalVars.color.brand : minimalVars.color.textSecondary)};
  background: ${({ $tone }) =>
    $tone === 'active' ? minimalVars.color.brandSoft : minimalVars.color.bgSurfaceAlt};
`

interface PaymentMethod {
  id: string
  glyph: string
  name: string
  hint: string
  tone: 'active' | 'available'
  badge: string
}

/**
 * Wallet rows are money, not food: they render a flat brand glyph, never a
 * dish thumbnail. Keeping the mapping here (rather than reaching for the
 * dish-photo thumbnail) is what stops a payment method from covering its own
 * label with a stock photo.
 */
const GLYPH_ICON: Record<string, ComponentType<{ size?: number }>> = {
  '\u{1F45B}': WalletIcon,
  '\u{1F4B8}': WalletIcon,
  '\u{1F4E5}': WalletIcon,
  '\u{1F4B3}': CardIcon,
  '\u{1F3E6}': BankIcon,
  '\u{1F35B}': BowlIcon,
}

const MoneyGlyph = ({ glyph, size = 20 }: { glyph: string; size?: number }) => {
  const Glyph = GLYPH_ICON[glyph] ?? WalletIcon
  return <Glyph size={size} />
}

/** Fixed square so a method row's icon can never push its copy off-screen. */
const MethodIcon = styled.span`
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  display: inline-grid;
  place-items: center;
  border-radius: ${minimalVars.radius.md};
  background: ${minimalVars.color.brandSoft};
  color: ${minimalVars.color.brand};
`

const PaymentMethodList = ({ methods }: { methods: readonly PaymentMethod[] }) => (
  <List>
    {methods.map((m) => (
      <MethodRow key={m.id}>
        <MethodIcon aria-hidden>
          <MoneyGlyph glyph={m.glyph} size={20} />
        </MethodIcon>
        <MethodMeta>
          <MethodTitleRow>
            <MethodName>{m.name}</MethodName>
            <MethodBadge $tone={m.tone}>{m.badge}</MethodBadge>
          </MethodTitleRow>
          <MethodHint>{m.hint}</MethodHint>
        </MethodMeta>
      </MethodRow>
    ))}
  </List>
)

/** Wallet tabs are deep-linkable (profile → “Payments” lands on methods). */
const useInitialTab = <T extends string>(fallback: T, valid: readonly T[]): T => {
  const [params] = useSearchParams()
  const requested = params.get('tab') as T | null
  return requested && valid.includes(requested) ? requested : fallback
}

/**
 * Wallet for ChooseChow. Customer view = balance + top-up + spending. Chef
 * view = earnings only — money flows IN from orders and OUT as payouts, with
 * no top-up affordance (chefs don't fund their wallet, the platform does).
 */
export const WalletPage = () => {
  const { isChef } = useChooseChow()
  return isChef ? <ChefWallet /> : <CustomerWallet />
}

/* ── customer view (unchanged behavior) ─────────────────────────────── */

const CustomerWallet = () => {
  const initialTab = useInitialTab<CustomerTab>('overview', ['overview', 'topup', 'transactions', 'methods'])
  const [tab, setTab] = useState<CustomerTab>(initialTab)
  const back = useBackNav()
  const { profileId } = useChooseChow()
  const balanceSnapshot = useProfileWalletBalanceSnapshot() as { records: readonly StoreRecord[] }
  const txnSnapshot = useWalletTransactionSnapshot() as { records: readonly StoreRecord[] }

  const myTxns = useMemo(() => {
    let list = txnSnapshot.records
    if (isLiveRuntime()) {
      list = list.filter((t) => {
        const id = String(t.id ?? '')
        if (id.startsWith('txn-') || id.includes('-prototype')) return false
        const ownerId = walletOwner(t)
        return ownerId === profileId
      })
    }
    return list
  }, [txnSnapshot.records, profileId])

  const myBalance = useMemo(() => {
    let list = balanceSnapshot.records
    if (isLiveRuntime()) {
      list = list.filter((b) => {
        const id = String(b.id ?? '')
        if (id.startsWith('balance-') || id.includes('-prototype')) return false
        return String(b.profile_id ?? '') === profileId
      })
    }
    return list
  }, [balanceSnapshot.records, profileId])

  const balanceRecord = myBalance[0]
  const balance = balanceRecord ? Number(balanceRecord.available_minor ?? 0) / 100 : (isLiveRuntime() ? 0 : 12500)

  const spentValue =
    myTxns
      .filter((t) => safeAmount(t) < 0)
      .reduce((sum, t) => sum + Math.abs(safeAmount(t)), 0) / 100
  const spent = spentValue > 0 ? spentValue : (isLiveRuntime() ? 0 : 4500)

  const addedValue =
    myTxns
      .filter((t) => safeAmount(t) > 0)
      .reduce((sum, t) => sum + safeAmount(t), 0) / 100
  const added = addedValue > 0 ? addedValue : (isLiveRuntime() ? 0 : 20000)

  const visibleTransactions = myTxns.map((t, index) => {
    const amount = Math.abs(safeAmount(t)) / 100
    const direction: 'in' | 'out' = safeAmount(t) > 0 ? 'in' : 'out'
    const glyph = direction === 'in' ? '📥' : '💸'
    const title = String(t.reference || (direction === 'in' ? 'Deposit' : 'Payment'))
    const sub = t.created_at ? new Date(String(t.created_at)).toLocaleDateString() : 'Today'
    return {
      id: String(t.id ?? index),
      glyph,
      title,
      sub,
      amount,
      direction,
    }
  })

  const handleTopUp = () => {
    const rawBalanceStore = prototypeStoreRegistry.get('billing.profile_wallet_balances')
    const rawTxnStore = prototypeStoreRegistry.get('wallet.wallet_transactions')
    if (!rawBalanceStore || !rawTxnStore) return

    const balanceStore = rawBalanceStore as unknown as TenantProjectionStore<StoreRecord>
    const txnStore = rawTxnStore as unknown as TenantProjectionStore<StoreRecord>

    const topUpAmountMinor = 500000
    const existing = balanceStore.getSnapshot().records[0]
    const currentAvailable = existing ? Number(existing.available_minor ?? 0) : 1250000
    const profileId = existing ? String(existing.profile_id) : 'profile-prototype'

    balanceStore.upsert({
      ...existing,
      profile_id: profileId,
      available_minor: currentAvailable + topUpAmountMinor,
      currency: 'NGN',
      owner_class: 'customer',
      pending_minor: 0,
    })
    txnStore.upsert({
      id: Math.random().toString(36).substring(2),
      amount_minor: topUpAmountMinor,
      currency: 'NGN',
      reference: 'Top Up via Paystack',
      rider_id: profileId,
      status: 'success',
      type: 'WALLET_TRANSACTION_TYPE_ADJUSTMENT',
      created_at: new Date().toISOString(),
    })
  }

  return (
    <Screen
      header={
        <PageHeader title="My Wallet" accentDot gradient={BALANCE_GRADIENT} onBack={back}>
          <BalanceCard
            embedded
            balance={balance}
            onTopUp={handleTopUp}
            onHistory={() => setTab('transactions')}
          />
        </PageHeader>
      }
    >
      <Tabs ariaLabel="Wallet sections" value={tab} options={customerTabs} onChange={(val) => setTab(val)} />
      {tab === 'overview' ? (
        <>
          <Tiles>
            <StatTile glyph={<MoneyGlyph glyph="💸" size={24} />} label="Spent" amount={spent} />
            <StatTile glyph={<MoneyGlyph glyph="📥" size={24} />} label="Added" amount={added} />
          </Tiles>
          <div style={{ marginTop: '24px', marginBottom: '12px' }}>
            <SectionHeading title="Recent transactions" />
          </div>
          <List>
            {visibleTransactions.map((txn) => (
              <TransactionRow
                key={txn.id}
                glyph={<MoneyGlyph glyph={txn.glyph} />}
                title={txn.title}
                sub={txn.sub}
                amount={txn.amount}
                direction={txn.direction}
              />
            ))}
          </List>
        </>
      ) : tab === 'methods' ? (
        <>
          <SectionHeading title="Ways to pay" />
          <PaymentMethodList
            methods={[
              {
                id: 'wallet',
                glyph: '👛',
                name: 'ChowWallet',
                hint: `Balance ₦${balance.toLocaleString()} · used first at checkout`,
                tone: 'active',
                badge: 'Default',
              },
              {
                id: 'card',
                glyph: '💳',
                name: 'Card via Paystack',
                hint: 'Debit or credit card, charged per order',
                tone: 'available',
                badge: 'Available',
              },
              {
                id: 'bank',
                glyph: '🏦',
                name: 'Bank account',
                hint: 'Pay by bank transfer at checkout or top-up',
                tone: 'available',
                badge: 'Available',
              },
            ]}
          />
        </>
      ) : (
        <MinimalEmptyState
          title={tab === 'transactions' ? 'No more transactions' : 'Coming soon'}
          description={
            tab === 'topup'
              ? 'Top up with Paystack, card, or transfer.'
              : 'Your full transaction history will appear here.'
          }
        />
      )}
    </Screen>
  )
}

/* ── chef view: earnings, payouts, bank — no top-up ─────────────────── */

const PayoutBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${minimalVars.space.xs};
  padding: ${minimalVars.space.xs};
  border-radius: ${minimalVars.radius.md};
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
`

const PayoutMeta = styled.div`
  display: grid;
  gap: 2px;
`

const PayoutLabel = styled.span`
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textTertiary};
`

const PayoutAmount = styled.span`
  font-family: ${minimalVars.typography.monoFamily};
  font-weight: ${minimalVars.typography.weightMedium};
  font-size: ${minimalVars.typography.bodySize};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  color: ${minimalVars.color.textPrimary};
`

const ChefWallet = () => {
  const [params, setParams] = useSearchParams()
  const weekOnly = params.get('period') === 'week'
  const initialTab = useInitialTab<ChefTab>('earnings', ['earnings', 'payouts', 'methods'])
  const [tab, setTab] = useState<ChefTab>(initialTab)
  const { profileId } = useChooseChow()
  // The payout ledger is the only authoritative record of chef money; see
  // chefPayouts.ts for why this no longer reads the chef wallet rows.
  const { data, loading, error, reload } = useChefPayouts()

  const earnedMinor = totalEarnedMinor(data)
  const sentMinor = data.sent_minor + data.paid_out_minor
  const earningRows = data.earnings.filter((e) => !weekOnly || isEarnedThisWeek(e.created_at))

  return (
    <Screen
      header={
        <PageHeader title="Earnings" accentDot gradient={BALANCE_GRADIENT}>
          <BalanceCard embedded balance={earnedMinor / 100} label="Earned so far" onHistory={() => setTab('payouts')} />
        </PageHeader>
      }
    >
      <Tabs ariaLabel="Wallet sections" value={tab} options={chefTabs} onChange={(val) => setTab(val)} />

      {error && (
        <MinimalAlert tone="danger" title="Earnings unavailable" action={<MinimalButton size="sm" onClick={() => void reload()}>Try again</MinimalButton>}>
          {error}
        </MinimalAlert>
      )}

      {tab === 'earnings' && (
        <>
          {weekOnly && <MinimalButton variant="ghost" onClick={() => setParams({ tab: 'earnings' })}>This week · Show all earnings</MinimalButton>}
          <Tiles>
            <StatTile glyph={<MoneyGlyph glyph="📥" size={24} />} label="This week" amount={weekEarnedMinor(data) / 100} />
            <StatTile glyph={<BankIcon size={24} />} label="Sent to your bank" amount={sentMinor / 100} />
            <StatTile glyph={<WalletIcon size={24} />} label="Held by ChooseChow" amount={data.held_minor / 100} />
            <StatTile glyph={<MoneyGlyph glyph="🚚" size={24} />} label="On the way" amount={data.releasing_minor / 100} />
          </Tiles>
          {data.held_minor > 0 && (
            <PayoutBar>
              <PayoutMeta>
                <PayoutLabel>ChooseChow is holding this until your bank account is active for payouts. It is paid automatically after that.</PayoutLabel>
                <PayoutAmount>₦{(data.held_minor / 100).toLocaleString()}</PayoutAmount>
              </PayoutMeta>
              <MinimalButton variant="secondary" onClick={() => setTab('methods')}>Bank</MinimalButton>
            </PayoutBar>
          )}
          {loading && earningRows.length === 0 ? (
            <MinimalEmptyState title="Loading earnings" description="Reading your paid orders…" />
          ) : earningRows.length === 0 ? (
            <MinimalEmptyState title="No earnings yet" description="Money from your orders shows up here once a customer pays." />
          ) : (
            <List>
              {earningRows.map((e) => (
                <TransactionRow
                  key={`${e.order_id}-${e.created_at}`}
                  glyph={<MoneyGlyph glyph={e.state === 'reversed' ? '↩️' : '🍛'} />}
                  title={`Order ${orderReference({ id: e.order_id })}`}
                  sub={`${earningStateLabel[e.state]} · ${e.created_at ? new Date(e.created_at).toLocaleDateString() : ''}`}
                  amount={e.amount_minor / 100}
                  direction={e.state === 'reversed' ? 'out' : 'in'}
                />
              ))}
            </List>
          )}
        </>
      )}

      {tab === 'payouts' && (
        <>
          <MethodHint as="p" style={{ margin: 0 }}>
            Each paid order’s share goes straight to your bank through Paystack, usually the next business day.
            Money ChooseChow held for you is paid out below.
          </MethodHint>
          {data.releases.length === 0 ? (
            <MinimalEmptyState title="No payouts of held money" description="Nothing has been held for you, or it has not been paid out yet." />
          ) : (
            <List>
              {data.releases.map((r) => (
                <TransactionRow
                  key={r.id}
                  glyph={<MoneyGlyph glyph="🏦" />}
                  title={releaseStatusLabel(r.status)}
                  sub={new Date(r.completed_at || r.created_at).toLocaleDateString()}
                  amount={r.amount_minor / 100}
                  direction="out"
                />
              ))}
            </List>
          )}
        </>
      )}

      {tab === 'methods' && (
        <>
          <SectionHeading title="Payout destination" />
          <PaymentMethodList
            methods={[
              {
                id: 'bank_transfer',
                glyph: '🏦',
                name: 'Nigerian Bank Account',
                hint: 'Paystack sends each order’s share to this account, usually the next business day',
                tone: 'active',
                badge: 'Settlement',
              },
            ]}
          />
          <div style={{ marginTop: '16px' }}>
            <SectionHeading title="Settlement bank details" />
            <ChefPayoutAccountForm profileId={profileId} />
          </div>
          <MethodHint as="p" style={{ margin: 0, marginTop: '12px' }}>
            Your account details are confirmed with Paystack for automatic settlement. For full kitchen onboarding and hygiene review, visit Kitchen Verification.
          </MethodHint>
        </>
      )}
    </Screen>
  )
}
